import Cookies from 'js-cookie';

export interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

interface CookieOptions {
  expires?: number;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

const COOKIE_SETTINGS_KEY = 'cookie_settings';
const COOKIE_CONSENT_KEY = 'cookie_consent';

const defaultSettings: CookieSettings = {
  necessary: true, // Always true as these are essential
  analytics: false,
  marketing: false,
  preferences: false
};

export const cookieManager = {
  // Initialize cookie settings
  init() {
    const settings = this.getSettings();
    if (!settings) {
      this.setSettings(defaultSettings);
    }
  },

  // Get current cookie settings
  getSettings(): CookieSettings | null {
    const settings = Cookies.get(COOKIE_SETTINGS_KEY);
    return settings ? JSON.parse(settings) : null;
  },

  // Set cookie settings
  setSettings(settings: CookieSettings, options?: CookieOptions) {
    Cookies.set(COOKIE_SETTINGS_KEY, JSON.stringify({
      ...settings,
      necessary: true // Ensure necessary cookies are always enabled
    }), {
      expires: 365, // Default to 1 year
      sameSite: 'strict',
      ...options
    });
  },

  // Check if user has given consent
  hasConsent(): boolean {
    return Cookies.get(COOKIE_CONSENT_KEY) === 'true';
  },

  // Set consent status
  setConsent(granted: boolean, options?: CookieOptions) {
    Cookies.set(COOKIE_CONSENT_KEY, granted.toString(), {
      expires: 365,
      sameSite: 'strict',
      ...options
    });
  },

  // Get a specific cookie
  get(key: string): string | undefined {
    return Cookies.get(key);
  },

  // Set a cookie with type checking
  set(key: string, value: string, type: keyof CookieSettings, options?: CookieOptions) {
    const settings = this.getSettings();
    if (!settings) return false;

    // Only set cookie if the corresponding setting is enabled
    if (type === 'necessary' || settings[type]) {
      Cookies.set(key, value, {
        sameSite: 'strict',
        ...options
      });
      return true;
    }
    return false;
  },

  // Remove a cookie
  remove(key: string, options?: CookieOptions) {
    Cookies.remove(key, options);
  },

  // Clear all non-essential cookies
  clearNonEssential() {
    const allCookies = Cookies.get();
    Object.keys(allCookies).forEach(key => {
      if (key !== COOKIE_SETTINGS_KEY && key !== COOKIE_CONSENT_KEY) {
        this.remove(key);
      }
    });
  }
};